<template>
  <v-container fluid class="main-container responsive-height">
    <v-row dense>
      <v-col lg="12" md="12" sm="12" cols="12">
        <v-sheet
          class="transactionTable pb-3"
          elevation="0"
          :color="$vuetify.theme.dark ? '#162334 ' : '#E8F1F8'"
        >
          <div class="title-bar pt-5">
            <div>
              <v-icon dense color="#5F7692">mdi-bag-checked</v-icon>
              <span class="ml-2">Change MT5 Trading Account Password</span>
            </div>
          </div>
        </v-sheet>
      </v-col>
    </v-row>

    <v-card
      class="deposit-form px-3 pb-10"
      :color="$vuetify.theme.dark ? '#172233' : '#E0EAF2'"
    >
      <v-row>
        <!-- Account Selection -->
        <v-col cols="12" sm="12" md="12" lg="9">
          <fieldset>
            <legend>Select Account No</legend>
            <v-select
              v-model="selectedAccount"
              :items="userleverages"
              :item-text="
                (item) =>
                  `${item.trading_account_no} (${item.tradingaccounttypes?.account_type_display})`
              "
              item-value="trading_account_no"
              label="MT5 Account"
              single-line
              solo
              hide-details
              append-icon="mdi-chevron-down"
              class="custom-select"
              :readonly="!isEditable"
            >
            </v-select>
          </fieldset>
        </v-col>

        <!-- Password Type Selection (NEW) -->
        <v-col cols="12" sm="12" md="12" lg="9">
          <fieldset>
            <legend>Password Type</legend>
            <v-select
              v-model="passwordType"
              :items="passwordTypes"
              item-text="text"
              item-value="value"
              label="Select Password Type"
              single-line
              solo
              hide-details
              append-icon="mdi-chevron-down"
              class="custom-select"
              :disabled="!selectedAccount"
            >
              <template v-slot:prepend-inner>
                <v-icon :color="passwordType ? 'primary' : 'grey'">
                  {{ passwordType === 'master' ? 'mdi-key' : passwordType === 'investor' ? 'mdi-key-variant' : 'mdi-key-outline' }}
                </v-icon>
              </template>
            </v-select>
          </fieldset>
        </v-col>

        <!-- Info Alert -->
        <v-col cols="12" sm="12" md="12" lg="9" v-if="passwordType">
          <v-alert
            :type="passwordType === 'master' ? 'info' : 'warning'"
            dense
            outlined
            border="left"
            class="mb-0"
          >
            <div class="text-body-2">
              <strong v-if="passwordType === 'master'">Master Password:</strong>
              <strong v-else>Investor Password:</strong>
              {{ passwordType === 'master' 
                ? 'Used for full trading access and account management' 
                : 'Used for read-only access to view trades and account status' 
              }}
            </div>
          </v-alert>
        </v-col>

        <!-- New Password -->
        <v-col cols="12" sm="12" md="12" lg="9">
          <fieldset>
            <legend>New {{ passwordType === 'master' ? 'Master' : passwordType === 'investor' ? 'Investor' : '' }} Password</legend>
            <v-text-field
              v-model="newPassword"
              :label="`New ${passwordType === 'master' ? 'Master' : passwordType === 'investor' ? 'Investor' : ''} Password`"
              :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showPassword ? 'text' : 'password'"
              @click:append="showPassword = !showPassword"
              single-line
              solo
              hide-details
              class="custom-select"
              :disabled="!passwordType"
            >
            </v-text-field>
          </fieldset>
        </v-col>

        <!-- Password Rules -->
        <v-row class="px-3">
          <v-col
            v-for="(rule, index) in passwordRules"
            :key="index"
            cols="auto"
            class="flex-wrap wrap"
          >
            <v-chip small :color="rule.met ? 'greenMain' : 'grey'" text-color="white">
              <v-icon small class="mr-1">{{
                rule.met ? "mdi-check" : "mdi-close"
              }}</v-icon>
              {{ rule.text }}
            </v-chip>
          </v-col>
        </v-row>

        <!-- Confirm Password -->
        <v-col cols="12" sm="12" md="12" lg="9">
          <fieldset>
            <legend>Confirm Password</legend>
            <v-text-field
              v-model="confirmPassword"
              label="Confirm Password"
              placeholder="Write Password Again"
              :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
              :type="showConfirmPassword ? 'text' : 'password'"
              @click:append="showConfirmPassword = !showConfirmPassword"
              single-line
              solo
              hide-details
              class="custom-select"
              :disabled="!newPassword"
            >
            </v-text-field>
          </fieldset>
        </v-col>

        <!-- Error Message & Submit Button -->
        <v-col cols="12" sm="12" md="12" lg="9">
          <v-alert v-if="errorMessage" type="error" class="mb-4">
            {{ errorMessage }}
          </v-alert>

          <v-btn
            color="greenMain"
            large
            tile
            elevation="0"
            class="ma-0 mt-0 text-capitalize btn-account"
            @click="savePassword"
            :loading="loading" 
            :disabled="loading || !isFormValid"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save {{ passwordType === 'master' ? 'Master' : passwordType === 'investor' ? 'Investor' : '' }} Password
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script>
import { LEVERAGE__ACTIONS } from "@/store/action-types";
import { mapActions, mapState } from "vuex";
import toast from "@/services/Notification";

export default {
  data: () => ({
    isEditable: true,
    selectedAccount: "",
    passwordType: null, // 'master' or 'investor'
    currentPassword: "",
    loading: false,
    newPassword: "",
    confirmPassword: "",
    showPassword: false,
    showConfirmPassword: false,
    errorMessage: "",
    
    passwordTypes: [
      { 
        text: "Master Password (Full Access)", 
        value: "master",
        icon: "mdi-key"
      },
      { 
        text: "Investor Password (Read-Only)", 
        value: "investor",
        icon: "mdi-key-variant"
      },
    ],
    
    passwordRules: [
      { text: "8 Characters", met: false },
      { text: "1 Lowercase", met: false },
      { text: "1 Uppercase", met: false },
      { text: "1 Digit", met: false },
      { text: "1 Special Character", met: false },
    ],
  }),

  computed: {
    ...mapState("leverage", ["userleverages"]),

    isFormValid() {
      return (
        this.selectedAccount &&
        this.passwordType &&
        this.newPassword &&
        this.confirmPassword &&
        this.newPassword === this.confirmPassword &&
        this.passwordRules.every(rule => rule.met)
      );
    },
  },

  watch: {
    newPassword() {
      this.validatePassword();
    },
    
    selectedAccount(newAccount) {
      const account = this.userleverages.find(
        (item) => item.trading_account_no === newAccount
      );
      if (account) {
        this.currentLeverage = `1:${account.leverage}`;
      } else {
        this.currentLeverage = "";
      }
      
      // Reset password type when account changes
      if (!this.isEditable) {
        this.passwordType = null;
      }
    },
  },

  methods: {
    ...mapActions("leverage", [
      LEVERAGE__ACTIONS.USERLEVERAGE,
      LEVERAGE__ACTIONS.UPDATEMT5PASSWORD,
    ]),

    validatePassword() {
      this.passwordRules[0].met = this.newPassword.length >= 8;
      this.passwordRules[1].met = /[a-z]/.test(this.newPassword);
      this.passwordRules[2].met = /[A-Z]/.test(this.newPassword);
      this.passwordRules[3].met = /\d/.test(this.newPassword);
      this.passwordRules[4].met = /[!@#$%^&*(),.?":{}|<>]/.test(
        this.newPassword
      );
    },

    async savePassword() {
      this.loading = true;
      this.errorMessage = "";

      try {
        // Validate passwords match
        if (this.newPassword !== this.confirmPassword) {
          this.errorMessage = "Passwords do not match!";
          return;
        }

        // Validate all password rules are met
        if (!this.passwordRules.every(rule => rule.met)) {
          this.errorMessage = "Password does not meet all requirements!";
          return;
        }

        // Prepare payload based on password type
        const payload = {
          login_acc: this.selectedAccount,
        };

        if (this.passwordType === 'master') {
          payload.new_password = this.newPassword;
        } else if (this.passwordType === 'investor') {
          payload.new_investor_password = this.newPassword;
        }

        // Call appropriate action based on password type
        if (this.passwordType === 'master') {
          await this[LEVERAGE__ACTIONS.UPDATEMT5PASSWORD](payload);
        } else if (this.passwordType === 'investor') {
          // You may need to create a new action for investor password
          // For now, using the same action with different payload
          await this[LEVERAGE__ACTIONS.UPDATEMT5PASSWORD](payload);
        }

        toast.success(
          `${this.passwordType === 'master' ? 'Master' : 'Investor'} password changed successfully!`
        );

        // Reset form
        this.resetForm();

      } catch (error) {
        console.error('Trading Account Password change error:', error);
        this.errorMessage = error.response?.data?.message || "An error occurred while updating the password.";
        toast.error(this.errorMessage);
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      if (this.isEditable) {
        this.selectedAccount = "";
      }
      this.passwordType = null;
      this.newPassword = "";
      this.confirmPassword = "";
      this.showPassword = false;
      this.showConfirmPassword = false;
      this.errorMessage = "";
    },
  },

  async mounted() {
    const account = this.$route.query.account;
    await this[LEVERAGE__ACTIONS.USERLEVERAGE]();

    if (account) {
      this.selectedAccount = account;
      this.isEditable = false;
    }
  },
};
</script>

<style scoped>
.text-body-2 {
  line-height: 1.6;
}

.v-chip {
  margin: 4px;
}
</style>